import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from '../AuthContext';
import { doc, getDoc, collection, getDocs, Timestamp, query, where, updateDoc, onSnapshot, QuerySnapshot, DocumentData, Unsubscribe, writeBatch } from 'firebase/firestore';
import { db, auth, storage } from "../firebaseConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { ref, getDownloadURL, StorageReference } from 'firebase/storage';
import Calendar from './Calendar';

// Supabase
import { supabase } from '../supabaseConfig';

// redux
import { updateScreen } from '../redux/screenSlice';
import { clearLeadManagementProps } from '../redux/leadManagementPropsSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch, useAppSelector } from "../redux/store";

import "react-datepicker/dist/react-datepicker.css";
import { FirebaseError } from "firebase/app";
import getCurrentScreen from "../Helper/GetScreen";

interface CustomClaims {
    companyId?: string;
    leadUid?: string;
    subAgentId?: string;
    role?: string;
}

interface Notification {
    id: string,
    companyId: string,
    leadId: string,
    createdBy: string,
    updatedBy: string,
    title: string,
    body: string,
    type: string,
    color: string,
    uri: string,
    isIntracted: boolean,
    createdAt: Timestamp,
}

// Demo data interface for search results
interface SearchLead {
    id: string;
    name: string;
    phone: string;
    assigned_to?: string;
    alternate_numbers: string[];
    email?: string;
    role?: string;
    uid?: string; // Add UID field for navigation
    is_active: boolean;
    collaborators: string[];
}

// Version is set from the build date at build time
const APP_VERSION = process.env.REACT_APP_BUILD_DATE || "local";

const TopNavbar: React.FC = () => {

    const { isAdmin, isManager, isStaff, currentUser, userData } = useAuth();
    const profileImage = `${process.env.PUBLIC_URL}/images/desktop/Ellipse 50.svg`;

    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const dispatch = useDispatch<AppDispatch>()

    const navigate = useNavigate();
    const location = useLocation();
    const [userName, setUserName] = useState<string>("");
    const [screensDropdownOpen, setScreensDropdownOpen] = useState(false);
    const [studprofdropdownOpen, setStudprofdropdownOpen] = useState(false);
    const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
    const [userPosition, setUserPosition] = useState<string>("Loading...");
    const [staffRoles, setStaffRoles] = useState<{ [key: string]: string }>({});
    const [allStaffs, setAllStaffs] = useState<{ id: string; name: string; managerIds: string[] }[]>([]);
    const [companyLogo, setCompanyLogo] = useState<string | null>(null);
    const [profilePicture, setProfilePicture] = useState<string>('');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const screensdropdownRef = useRef<HTMLDivElement | null>(null);
    const studprofdropdownRef = useRef<HTMLDivElement | null>(null);
    const [isLogoLoading, setIsLogoLoading] = useState(true);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [userRole, setUserRole] = useState<string>('');
    const [isValidityModalOpen, setIsValidityModalOpen] = useState(false);

    // Validity related states
    const [validityDate, setValidityDate] = useState<Date | null>(null);
    const [isValidityExpired, setIsValidityExpired] = useState<boolean>(false);
    const [timeLeft, setTimeLeft] = useState<{ days: number, hours: number, minutes: number, seconds: number } | null>(null);

    // Search related states
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchResults, setSearchResults] = useState<SearchLead[]>([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const searchDropdownRef = useRef<HTMLDivElement | null>(null);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

    const [notificationsLimit, setNotificationsLimit] = useState<number>(10);
    const [hasMoreNotifications, setHasMoreNotifications] = useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const notificationScrollRef = useRef<HTMLDivElement | null>(null);

    // Debouncing effect for search
    useEffect(() => {
        console.log('Search debounce effect triggered with searchQuery:', searchQuery);

        if (searchQuery.trim() === '') {
            setDebouncedSearchQuery('');
            return;
        }

        const timeoutId = setTimeout(() => {
            console.log('=== DEBOUNCE TIMEOUT EXECUTED ===');
            console.log('Setting debouncedSearchQuery to:', searchQuery.trim());
            setDebouncedSearchQuery(searchQuery.trim());
        }, 300);

        console.log('Debounce timeout set with ID:', timeoutId);

        return () => {
            console.log('Cleaning up debounce timeout:', timeoutId);
            clearTimeout(timeoutId);
        };
    }, [searchQuery]);

    // Effect to perform search when debounced query changes
    useEffect(() => {
        console.log('Search effect triggered with debouncedSearchQuery:', debouncedSearchQuery);

        if (debouncedSearchQuery) {
            console.log('About to call performSearch with:', debouncedSearchQuery);
            performSearch(debouncedSearchQuery).catch((error) => {
                console.error("Error in performSearch:", error);
                setSearchError('Search failed. Please try again.');
                setSearchLoading(false);
            });
        }
    }, [debouncedSearchQuery]);

    useEffect(() => {

        // Fetch user role from custom claims
        const fetchUserRole = async () => {
            const user = auth.currentUser;
            if (user) {
                const idTokenResult = await user.getIdTokenResult();
                const claims = idTokenResult.claims as CustomClaims;
                setUserRole(claims.role || '');
            }
        };

        fetchUserRole();
    }, []);

    useEffect(() => {
        // Fetch all users of the company.
        const fetchCompanyUsers = async () => {
            if (!companyId) return;
            try {
                const usersCollection = collection(db, "users");
                const q = query(usersCollection, where("companyId", "==", companyId));
                const querySnapshot = await getDocs(q);
                const users: { id: string; name: string; managerIds: string[] }[] = [];
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    users.push({ id: doc.id, name: data.name || 'No Name', managerIds: data.managerIds || [] });
                });
                setAllStaffs(users);
            } catch (error) {
                console.error("Error fetching company users:", error);
                setAllStaffs([]);
            }
        };

        fetchCompanyUsers();
    }, [companyId]);

    // Function to return staff name by ID
    const getStaffNameById = (staffId: string): string => {
        if (!staffId) return 'Unassigned';
        if (!allStaffs || allStaffs.length === 0) return 'Unknown Staff';
        const staff = allStaffs.find(s => s.id === staffId);
        return staff ? staff.name : 'Unknown Staff';
    };

    // Function check if lead is available for this user
    // if user is admin then return true
    // if user is manager check managerId of the assigned_to staff
    // if user is staff then check assigned_to
    // if user is present in collaborators array then return true
    const isLeadAccessibleByUser = (leadAssignedTo: string | undefined, collaborators?: string[]): boolean => {
        if (!currentUser?.uid) return false;

        // Check if the current user is present in the collaborators list
        if (collaborators && collaborators.includes(currentUser.uid)) {
            return true;
        }

        if (!leadAssignedTo) return false;

        // Admin can access all leads regardless of current screen
        if (isAdmin && currentScreen === 'Admin') {
            return true;
        }

        // Staff can only access leads assigned to them
        if (isStaff && currentScreen === 'Staff') {
            return leadAssignedTo === currentUser.uid;
        }


        // Manager can access:
        // 1. Leads assigned to themselves
        // 2. Leads assigned to staff under their management
        if (isManager && currentScreen === 'Manager') {
            // Check if lead is assigned to the manager directly
            if (leadAssignedTo === currentUser.uid) {
                return true;
            }

            // Check if lead is assigned to a staff member under this manager
            const assignedStaff = allStaffs.find(s => s.id === leadAssignedTo);
            if (assignedStaff && assignedStaff.managerIds?.includes(currentUser.uid)) {
                return true;
            }
        }

        return false;
    };

    // Search functionality with debouncing
    const performSearch = async (query: string) => {
        console.log('performSearch called with query:', query);

        if (query.trim() === '') {
            setSearchResults([]);
            setShowSearchDropdown(false);
            setSearchLoading(false);
            return;
        }

        setSearchLoading(true);
        setShowSearchDropdown(true);
        setSearchError(null);

        try {
            console.log('Supabase client loaded successfully');

            // Search in Supabase leads table using RPC (your original working version)
            const { data, error } = await supabase
                .rpc('search_leads_comprehensive', { search_term: query })
                .eq('company_id', companyId)
                .limit(10);

            console.log('Supabase search result:', { data, error, query });

            if (error) {
                console.error('Supabase search error:', error);
                setSearchError('Failed to search students. Please try again.');
                setSearchResults([]);
            } else {
                // Transform Supabase data to SearchLead format
                const transformedResults: SearchLead[] = (data || []).map((lead: any) => ({
                    id: lead.id,
                    name: lead.name || 'Unknown',
                    phone: lead.number || '',
                    email: lead.email || '',
                    role: 'Student',
                    uid: lead.firestore_uuid,
                    assigned_to: lead.assigned_to || '',
                    alternate_numbers: lead.alternate_numbers || [],
                    is_active: lead.is_active,
                    collaborators: lead.collaborators || [],
                }));

                setSearchResults(transformedResults);
                setSearchError(null);
            }
        } catch (error) {
            console.error('Error searching leads:', error);
            setSearchError('Connection error. Please check your internet connection.');
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        console.log('handleSearchChange called with query:', query);
        setSearchQuery(query);

        if (query.trim() === '') {
            setSearchResults([]);
            setShowSearchDropdown(false);
            setSearchLoading(false);
            setDebouncedSearchQuery('');
            return;
        }
    };

    const handleSearchLeadSelect = async (lead: SearchLead) => {
        console.log('Selected lead:', lead);

        try {
            if (lead.uid) {
                // check if lead is accessible by this user
                if (!isLeadAccessibleByUser(lead.assigned_to, lead.collaborators)) {
                    console.error('User is not authorized to access this lead');
                    alert('You are not authorized to access this student.');
                    return;
                }
                // Navigate directly using the UID from search results
                navigate(`/studentdetails/${lead.uid}`);
            } else {
                // Dynamically import supabase for fallback
                const { supabase } = await import('../supabaseConfig');

                // Fallback: fetch UID from Supabase if not available in search results
                const { data, error } = await supabase
                    .from('leads')
                    .select('firestore_uuid')
                    .eq('id', lead.id)
                    .single();

                if (error) {
                    console.error('Error fetching lead UID:', error);
                    // Handle error - maybe show a toast notification
                    return;
                }

                if (data?.firestore_uuid) {
                    navigate(`/studentdetails/${data.firestore_uuid}`);
                } else {
                    console.error('No UID found for lead');
                    // Handle case where UID is not available
                }
            }
        } catch (error) {
            console.error('Error navigating to lead details:', error);
        } finally {
            // Clear search state
            setSearchQuery('');
            setSearchResults([]);
            setShowSearchDropdown(false);
            setIsSearchOpen(false);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchDropdown(false);
    };

    const toogleScreensDropdown = () => {
        setScreensDropdownOpen(!screensDropdownOpen);
    }
    const toggleStudprofdropdown = () => {
        setStudprofdropdownOpen(!studprofdropdownOpen);
    };

    const screenStateHandler = (screen: string) => {
        if (screen === 'Admin') {
            dispatch(updateScreen('Admin'));
            navigate('/admin/dashboard');
        } else if (screen === 'Manager') {
            dispatch(updateScreen('Manager'));
            navigate('/manager/dashboard');
        } else if (screen === 'Staff') {
            dispatch(updateScreen('Staff'));
            navigate('/staff/dashboard');
        } else if (screen === 'SubAgent') {
            dispatch(updateScreen('subAgent'));
            navigate('/subagent/dashboard');
        }
    };

    const getPageName = (path: string, currentScreen: string = 'User'): string => {
        const normalizedPath = path.replace(/\/$/, '');

        if (normalizedPath === '/students') {
            return currentScreen === 'Admin'
                ? 'All Students'
                : currentScreen === 'Manager'
                    ? 'Students'
                    : 'My Students';
        }

        const PAGE_NAMES: { [key: string]: string } = {
            '/dashboard': 'Dashboard',
            '/teammanagement': 'Team Management',
            '/ticket': 'Tickets',
            '/escalation': 'Escalations',
            '/manager/escalation': 'Escalations',
            '/accounts': 'Accounts',
            '/student-dashboard': 'My Dashboard',
            '/student-profile': 'My Profile',
            '/student-services': 'Services',
            '/student-documents': 'Documents',
            '/student-raise-tickets': 'Tickets',
            '/student-escalations': 'Student Escalations',
            '/staffstask': 'My Tasks',
            '/home': 'Call CRM',
            '/settings': 'Settings',
            '/accounts/invoice': 'New Invoice',
            '/accounts/payouts': 'Payouts',
            '/accounts/invoicelist': 'Invoice List',
            '/leadmanagement': 'Lead Management',
            '/crm/leadmanagement': 'Lead Management',
            '/followups': 'Follow Ups',
            '/collaborations': 'Collaborations',
            '/invoices': 'Invoices',
            '/service': 'Services',
            '/studentdetails': 'Student Details', // add if desired
        };

        // Try exact match first
        if (PAGE_NAMES[normalizedPath]) {
            return PAGE_NAMES[normalizedPath];
        }

        // Try startsWith matching
        for (const [routePath, pageName] of Object.entries(PAGE_NAMES)) {
            if (normalizedPath.startsWith(routePath)) {
                return pageName;
            }
        }

        // Fallback: use the first path segment only
        const segments = normalizedPath.split('/').filter(Boolean);
        if (segments.length > 0) {
            const firstSegment = segments[0];

            const formatted = firstSegment
                .split('-')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                .join(' ');

            return formatted;
        }

        return 'Dashboard';
    };

    useEffect(() => {
        const fetchCompanyLogo = async (companyId: string) => {
            setIsLogoLoading(true);
            try {
                const logoRef: StorageReference = ref(storage, `companies/logos/${companyId}.png`);
                const url = await getDownloadURL(logoRef);
                setCompanyLogo(url);
            } catch (error) {
                console.log("No company logo found or error fetching logo");
                setCompanyLogo(null);
            } finally {
                setIsLogoLoading(false);
            }
        };

        if (companyId) {
            fetchCompanyLogo(companyId);
        }
    }, [companyId]);

    // fetch profile picture
    useEffect(() => {
        const fetchProfilePicture = async (userId: string) => {
            try {
                if (!userData || !userData.photo) {
                    console.error("User data or photo is missing");
                    setProfilePicture('');
                    return;
                }
                const profilePicRef: StorageReference = ref(storage, `/companies/staff_profiles/${companyId}/${userId}/${userData.photo}`);
                console.log(">>>>>>>>>>", profilePicRef);

                const url = await getDownloadURL(profilePicRef);
                setProfilePicture(url);
            } catch (error) {
                console.error("Error fetching user profile picture: ", error);
                setProfilePicture('');
            }
        };

        if (companyId && currentUser?.uid) {
            fetchProfilePicture(currentUser.uid);
        }
    }, [companyId]);

    useEffect(() => {
        const fetchCompanyId = async () => {
            const user = auth.currentUser;
            if (user) {
                const idTokenResult = await user.getIdTokenResult();
                const claims = idTokenResult.claims as CustomClaims;

                if (claims.role === 'student' || claims.role === 'subAgent') {
                    if (claims.companyId) {
                        setCompanyId(claims.companyId);
                    } else {
                        console.error("Company ID not found in claims");
                        setCompanyId(null);
                    }
                } else {
                    // Original approach for staff
                    const userDocRef = doc(db, "users", user.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data();
                        setCompanyId(userData.companyId);
                    } else {
                        console.error("User document not found");
                        setCompanyId(null);
                    }
                }
            } else {
                console.error("User is not authenticated");
                setCompanyId(null);
            }
        };
        fetchCompanyId();
    }, []);

    // Fetch company validity data
    useEffect(() => {
        const fetchCompanyValidity = async () => {
            if (!companyId) return;

            try {
                const companyDocRef = doc(db, "companies", companyId);
                const companyDocSnap = await getDoc(companyDocRef);

                if (companyDocSnap.exists()) {
                    const companyData = companyDocSnap.data();
                    console.log("Company data for validity:", companyData); // Debug log
                    const validityTimestamp = companyData.validity;

                    if (validityTimestamp) {
                        // Convert Firestore timestamp to Date
                        const validityEndDate = validityTimestamp.toDate();
                        setValidityDate(validityEndDate);

                        // Check if validity has expired
                        const currentDate = new Date();
                        setIsValidityExpired(currentDate > validityEndDate);

                        console.log("Validity processed:", {
                            validityEndDate,
                            isExpired: currentDate > validityEndDate
                        }); // Debug log
                    } else {
                        console.log("No validity data found for company");
                        setValidityDate(null);
                        setIsValidityExpired(false);
                    }
                } else {
                    console.error("Company document not found");
                }
            } catch (error) {
                console.error("Error fetching company validity:", error);
            }
        };

        fetchCompanyValidity();
    }, [companyId]);

    // Countdown timer effect
    useEffect(() => {
        if (!validityDate || isValidityExpired) {
            setTimeLeft(null);
            return;
        }

        const updateCountdown = () => {
            const now = new Date().getTime();
            const validityTime = validityDate.getTime();
            const difference = validityTime - now;

            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((difference % (1000 * 60)) / 1000);

                setTimeLeft({ days, hours, minutes, seconds });
            } else {
                setTimeLeft(null);
                setIsValidityExpired(true);
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [validityDate, isValidityExpired]);

    useEffect(() => {
        const fetchUserName = async () => {
            const user = auth.currentUser;
            if (user) {

                const idTokenResult = await user.getIdTokenResult();
                const claims = idTokenResult.claims as CustomClaims;

                if (!idTokenResult) {
                    console.error("No ID token result found");
                    setUserName("Unknown");
                    return;
                }

                if (claims.role === 'student') {
                    if (claims.companyId && claims.leadUid) {
                        const leadDocRef = doc(db, `companies/${claims.companyId}/leads/${claims.leadUid}`);
                        const leadDoc = await getDoc(leadDocRef);
                        if (leadDoc.exists()) {
                            const leadData = leadDoc.data();
                            setUserName(leadData.name || "Unknown");
                        } else {
                            console.error("Lead document not found");
                            setUserName("Unknown");
                        }
                    } else {
                        console.error("Company ID or Lead UID not found in claims");
                        setUserName("Unknown");
                    }
                } else if (claims.role === 'subAgent') {
                    if (claims.companyId && claims.subAgentId) {
                        try {
                            const subAgentDocRef = doc(db, `companies/${claims.companyId}/subAgents/${claims.subAgentId}`);
                            const subAgentDoc = await getDoc(subAgentDocRef);

                            if (!subAgentDoc.exists()) {
                                console.error("SubAgent document not found");
                                navigate('/');
                                return;
                            }

                            const subAgentData = subAgentDoc.data();
                            setUserName(subAgentData.name || "Unknown");
                        } catch (error) {
                            console.error("Error fetching sub-agent document:", error);

                            // Handle insufficient permissions
                            if (error instanceof FirebaseError && error.code === 'permission-denied') {
                                console.warn("Insufficient permissions");
                                navigate('/');
                                return;
                            }

                            setUserName("Unknown");
                            navigate('/');
                        }
                    } else {
                        console.error("Company ID or SubAgent ID not found in claims");
                        setUserName("Unknown");
                    }
                } else {
                    // Original approach for staff
                    const userDocRef = doc(db, "users", user.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data();
                        setUserName(userData.name || "User");
                    } else {
                        console.error("User document not found");
                        setUserName("Unknown");
                    }
                }
            } else {
                console.error("User is not authenticated");
                setUserName("Unknown");
            }
        };
        fetchUserName();
    }, [auth.currentUser]);

    useEffect(() => {
        const fetchUserStaffRole = async () => {
            const user = auth.currentUser;
            if (user) {
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    const claims = idTokenResult.claims as CustomClaims;

                    if (claims.role === 'student') {
                        setUserPosition('Student');
                    } else if (claims.role === 'subAgent') {
                        setUserPosition('Affiliate Partner');
                    } else {
                        // Original approach for staff
                        const userDocRef = doc(db, "users", user.uid);
                        const userDocSnap = await getDoc(userDocRef);

                        if (userDocSnap.exists()) {
                            const userData = userDocSnap.data();
                            const staffRoleId = userData.staffRoleId;
                            const staffRolesRef = collection(db, 'staffRoles');
                            const staffRolesSnapshot = await getDocs(staffRolesRef);
                            const rolesMap: { [key: string]: string } = {};
                            staffRolesSnapshot.forEach((doc) => {
                                rolesMap[doc.id] = doc.data().roleName || doc.data().name || doc.data().role;
                            });
                            setStaffRoles(rolesMap);
                            if (staffRoleId && rolesMap[staffRoleId]) {
                                setUserPosition(rolesMap[staffRoleId]);
                            } else {
                                setUserPosition("User");
                            }
                        } else {
                            console.error("User document not found");
                            setUserPosition("User");
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user staff role:", error);
                    setUserPosition("User");
                }
            } else {
                console.error("User is not authenticated");
                setUserPosition("User");
            }
        };

        fetchUserStaffRole();
    }, []);

    // Helper function to format validity date and calculate remaining days
    const getValidityStatus = () => {
        if (!validityDate) return null;

        const currentDate = new Date();
        const timeDifference = validityDate.getTime() - currentDate.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

        return {
            daysRemaining: daysDifference,
            isExpired: daysDifference <= 0,
            isExpiringSoon: daysDifference <= 7 && daysDifference > 0,
            formattedDate: validityDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        };
    };

    const validityStatus = getValidityStatus();

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (screensdropdownRef.current && !screensdropdownRef.current.contains(event.target as Node)) {
                setScreensDropdownOpen(false);
            }
            if (studprofdropdownRef.current && !studprofdropdownRef.current.contains(event.target as Node)) {
                setStudprofdropdownOpen(false);
            }
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node)) {
                setShowSearchDropdown(false);
            }
            if (document.getElementById('dropdown-menu') && !document.getElementById('dropdown-menu')?.contains(event.target as Node)) {
                // setNotificationDropdownOpen(false);
                const dropdownMenu = document.getElementById('dropdown-menu');
                if (dropdownMenu) {
                    // dropdownMenu.classList.toggle('hidden');
                    setNotificationDropdownOpen(false);
                    dropdownMenu.classList.add('hidden');
                }
            }
        }

        function handleGlobalClick(event: MouseEvent) {
            // Auto-logout if validity has expired and user clicks anywhere
            if (validityStatus?.isExpired && (isAdmin || isManager || isStaff)) {
                console.log('License expired - logging out user');
                handleLogout();
                return;
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('click', handleGlobalClick);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('click', handleGlobalClick);
        };
    }, [validityStatus, isAdmin, isManager, isStaff]);

    const handleLogout = () => {
        // remove all redux state
        dispatch(clearLeadManagementProps());
        dispatch(updateScreen(''));

        // Clear cookies and local storage
        document.cookie.split(";").forEach((c) => {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
        });
        localStorage.clear();
        sessionStorage.clear();

        // Sign out from Firebase
        auth.signOut().then(() => {
            navigate('/');  // Redirect to home page after logout
        }).catch((error) => {
            console.error("Error during logout: ", error);
        });
    };

    // Notification related functions
    const [notifications, setNotifications] = useState<Notification[]>([]);

    // Real-time function to listen to notifications changes
    const setupNotificationsListener = (
        callback: (notifications: Notification[]) => void,
        limit: number
    ): Unsubscribe | null => {
        const user = auth.currentUser;
        if (user) {
            const userDocRef = doc(db, "users", user.uid);
            const notificationQuery = query(
                collection(userDocRef, "notifications"),
                where("companyId", "==", companyId),
                where("isIntracted", "==", false),
                // orderBy("createdAt", "desc"), // Uncomment if you have an index
                // limit(limit) // Uncomment to enable server-side limiting
            );

            // Set up real-time listener
            const unsubscribe = onSnapshot(
                notificationQuery,
                (snapshot: QuerySnapshot<DocumentData>) => {
                    const notificationsData: Notification[] = [];
                    snapshot.forEach((doc) => {
                        const notification = doc.data() as Notification;
                        notificationsData.push({
                            id: doc.id,
                            companyId: notification.companyId,
                            leadId: notification.leadId,
                            createdBy: notification.createdBy,
                            updatedBy: notification.updatedBy,
                            title: notification.title,
                            body: notification.body,
                            type: notification.type,
                            color: notification.color,
                            uri: notification.uri,
                            isIntracted: notification.isIntracted,
                            createdAt: notification.createdAt,
                        });
                    });

                    // Sort notifications by createdAt in descending order
                    notificationsData.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());

                    // Check if there are more notifications than the current limit
                    setHasMoreNotifications(notificationsData.length > limit);

                    // Slice to the current limit for display
                    const limitedNotifications = notificationsData.slice(0, limit);

                    // Call the callback with updated data
                    callback(limitedNotifications);
                },
                (error: Error) => {
                    console.error("Error listening to notifications:", error);
                }
            );

            return unsubscribe;
        }
        return null;
    };


    // new function to handle scroll events
    const handleNotificationScroll = useCallback(() => {
        const scrollContainer = notificationScrollRef.current;
        if (!scrollContainer || isLoadingMore || !hasMoreNotifications) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

        // Check if user has scrolled to bottom (with 50px threshold)
        if (scrollHeight - scrollTop - clientHeight < 50) {
            setIsLoadingMore(true);

            // Simulate a small delay for better UX
            setTimeout(() => {
                setNotificationsLimit(prev => prev + 10);
                setIsLoadingMore(false);
            }, 300);
        }
    }, [isLoadingMore, hasMoreNotifications]);

    useEffect(() => {
        const scrollContainer = notificationScrollRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleNotificationScroll);
            return () => {
                scrollContainer.removeEventListener('scroll', handleNotificationScroll);
            };
        }
    }, [handleNotificationScroll]);

    const clearAllNotifications = async () => {
        const user = auth.currentUser;
        if (!user) return;

        try {
            const userDocRef = doc(db, "users", user.uid);
            const notificationQuery = query(
                collection(userDocRef, "notifications"),
                where("companyId", "==", companyId),
                where("isIntracted", "==", false)
            );

            const querySnapshot = await getDocs(notificationQuery);
            const batch = writeBatch(db);

            querySnapshot.forEach((doc) => {
                const notificationRef = doc.ref;
                batch.update(notificationRef, { isIntracted: true });
            });

            await batch.commit();
            console.log("All notifications marked as read");
        } catch (error) {
            console.error("Error clearing notifications:", error);
        }
    };

    // Updated useEffect with real-time listener
    useEffect(() => {
        let unsubscribe: Unsubscribe | null = null;

        if (currentUser) {
            // Set up the real-time listener with limit
            unsubscribe = setupNotificationsListener((notificationsData: Notification[]) => {
                setNotifications(notificationsData);
            }, notificationsLimit);
        }

        // Cleanup function
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [currentUser, companyId, notificationsLimit]);

    const unreadCount = notifications.filter(n => !n.isIntracted).length;

    const handelNotificationClick = async (notification: Notification) => {
        // Update the notification to mark it as read
        const user = auth.currentUser;
        if (user) {
            const userDocRef = doc(db, "users", user.uid);
            const notificationDocRef = doc(userDocRef, "notifications", notification.id);
            await updateDoc(notificationDocRef, {
                isIntracted: true,
            });

            // Navigate to the notification URI
            navigate(notification.uri);
            setNotificationDropdownOpen(false);
            const dropdownMenu = document.getElementById('dropdown-menu');
            if (dropdownMenu) {
                if (!notificationDropdownOpen) {
                    dropdownMenu.classList.remove('hidden');
                } else {
                    dropdownMenu.classList.add('hidden');
                }
            }
        }
    };

    return (
        <>
            <nav className="bg-zinc-100 w-full py-6 shadow-md fixed top-0 left-0 right-0 z-[10]">
                <div className="w-[95%] mx-auto flex justify-between items-center h-18">
                    <div className="flex flex-col items-start">
                        <div className="flex items-center mb-2">
                            {isLogoLoading ? (
                                <div className="w-15 h-12 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : companyLogo ? (
                                <img
                                    src={companyLogo}
                                    alt="Company Logo"
                                    className="w-15 h-12 object-contain cursor-pointer"
                                />
                            ) : (
                                <div className="w-15 h-12 bg-gray-300 flex items-center justify-center">
                                    <span className="text-sm text-gray-500"></span>
                                </div>
                            )}
                        </div>
                        <div className="text-[#02385E] font-montserrat text-[16px] font-normal leading-[19.5px] text-left">
                            Home / {getPageName(location.pathname, currentScreen)}
                        </div>
                    </div>

                    {/* Welcome message or Search bar */}
                    {/* Welcome message with animation */}
                    <div
                        className={`flex items-center space-x-1 transition-all duration-500 ease-in-out ${isSearchOpen ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0'
                            }`}
                    >
                        <span className="text-gray-700">Welcome,</span>
                        <span className="text-gray-700 text-2xl font-semibold">{userName}!</span>
                    </div>

                    <div className="flex items-center space-x-6 mt-4">
                        {/* Validity Button - positioned on the left side - only show when less than 3 days */}
                        {validityStatus && (isAdmin || isManager || isStaff) && (validityStatus.daysRemaining <= 7) && (
                            <button
                                onClick={() => setIsValidityModalOpen(true)}
                                className="flex items-center space-x-2 px-3 py-2 bg-white rounded-lg shadow-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer border border-orange-200 hover:border-orange-300"
                            >
                                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                                <span className="text-orange-700">
                                    {validityStatus.isExpired
                                        ? 'License Expired'
                                        : `License expires in ${validityStatus.daysRemaining} day${validityStatus.daysRemaining !== 1 ? 's' : ''}`
                                    }
                                </span>
                            </button>
                        )}

                        {/* Search bar and button (combined for animation) */}
                        <div className="relative flex items-center gap-2 w-auto">
                            <div
                                className={`absolute right-14 top-0 transform transition-all duration-500 ease-in-out origin-right ${isSearchOpen ? 'translate-x-0 opacity-100 scale-x-100' : 'translate-x-10 opacity-0 scale-x-0'
                                    }`}
                            >
                                <div className="flex items-center justify-between w-[500px] h-12 bg-white rounded-full shadow-lg px-4">
                                    <input
                                        type="text"
                                        placeholder={allStaffs.length === 0 ? "Wait a min..." : "Search students by name, phone, email..."}
                                        className="flex-1 bg-transparent outline-none text-gray-700"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        autoFocus={isSearchOpen}
                                        disabled={allStaffs.length === 0}
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={clearSearch}
                                            className="ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                                        >
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                    <svg
                                        className="w-5 h-5 text-gray-400 ml-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>

                                {/* Search Results Dropdown */}
                                {showSearchDropdown && allStaffs.length > 0 && (
                                    <div
                                        ref={searchDropdownRef}
                                        className="absolute top-full left-0 w-[500px] mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto"
                                    >
                                        {searchLoading ? (
                                            <div className="px-4 py-6 text-center text-gray-500">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                                <p className="text-sm">Searching students...</p>
                                            </div>
                                        ) : searchError ? (
                                            <div className="px-4 py-6 text-center text-red-500">
                                                <svg className="w-12 h-12 mx-auto text-red-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                                </svg>
                                                <p className="text-sm">{searchError}</p>
                                                <button
                                                    onClick={() => {
                                                        setSearchError(null);
                                                        if (searchQuery.trim()) {
                                                            performSearch(searchQuery);
                                                        }
                                                    }}
                                                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                                                >
                                                    Try again
                                                </button>
                                            </div>
                                        ) : searchResults.length > 0 ? (
                                            <div className="py-2">
                                                {searchResults.map((lead) => (
                                                    <div
                                                        key={lead.id}
                                                        onClick={() => handleSearchLeadSelect(lead)}
                                                        className={`flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${isLeadAccessibleByUser(lead.assigned_to, lead.collaborators) ? '' : 'opacity-50 cursor-not-allowed'}`}
                                                        title={isLeadAccessibleByUser(lead.assigned_to, lead.collaborators) ? '' : 'You are not authorized to access this student.'}
                                                    >
                                                        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-medium mr-3">
                                                            {lead.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between">
                                                                <h4 className="text-sm font-semibold text-gray-900 truncate">
                                                                    {lead.name}
                                                                </h4>
                                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                                    {lead.role}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-blue-600 font-medium">
                                                                {lead.phone || 'No phone'}
                                                                {lead.alternate_numbers?.length > 0 && (
                                                                    <span className="text-gray-400"> | {lead.alternate_numbers.join(', ')}</span>
                                                                )}
                                                            </p>
                                                            {lead.email && (
                                                                <p className="text-xs text-gray-500 truncate">
                                                                    {lead.email}
                                                                </p>
                                                            )}
                                                            {lead.assigned_to && (
                                                                <p className="text-xs text-green-600 truncate">
                                                                    Assigned To: {getStaffNameById(lead?.assigned_to)}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="flex-shrink-0 ml-2">
                                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : searchQuery.trim() !== '' && !searchLoading ? (
                                            <div className="px-4 py-6 text-center text-gray-500">
                                                <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                <p className="text-sm">No students found matching "{searchQuery}"</p>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>

                            {/* Toggle Button */}
                            <button
                                onClick={() => setIsSearchOpen(!isSearchOpen)}
                                className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 z-10"
                                title={isSearchOpen ? "Close Search" : "Open Search"}
                            >
                                <svg
                                    className={`w-6 h-6 text-gray-600 transform transition-transform duration-300 ${isSearchOpen ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d={isSearchOpen
                                            ? "M6 18L18 6M6 6l12 12" // X icon
                                            : "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" // Search icon
                                        }
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Calendar Button */}
                        {userRole !== 'subAgent' && (
                            <div className="relative">
                                <button
                                    onClick={() => setIsCalendarOpen(true)}
                                    className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                                    title="Open Calendar"
                                >
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M10 21H6.2C5.0799 21 4.51984 21 4.09202 20.782C3.71569 20.5903 3.40973 20.2843 3.21799 19.908C3 19.4802 3 18.9201 3 17.8V8.2C3 7.0799 3 6.51984 3.21799 6.09202C3.40973 5.71569 3.71569 5.40973 4.09202 5.21799C4.51984 5 5.0799 5 6.2 5H17.8C18.9201 5 19.4802 5 19.908 5.21799C20.2843 5.40973 20.5903 5.71569 20.782 6.09202C21 6.51984 21 7.0799 21 8.2V10M7 3V5M17 3V5M3 9H21M13.5 13.0001L7 13M10 17.0001L7 17M14 21L16.025 20.595C16.2015 20.5597 16.2898 20.542 16.3721 20.5097C16.4452 20.4811 16.5147 20.4439 16.579 20.399C16.6516 20.3484 16.7152 20.2848 16.8426 20.1574L21 16C21.5523 15.4477 21.5523 14.5523 21 14C20.4477 13.4477 19.5523 13.4477 19 14L14.8426 18.1574C14.7152 18.2848 14.6516 18.3484 14.601 18.421C14.5561 18.4853 14.5189 18.5548 14.4903 18.6279C14.458 18.7102 14.4403 18.7985 14.405 18.975L14 21Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                                </button>
                            </div>
                        )}

                        {/* Notification */}
                        {isAdmin || isManager || isStaff ? (
                            <div>
                                {/* notification svg */}
                                <div className="flex items-center gap-4">
                                    <div className="relative inline-block">
                                        <div className={`relative group bg-white rounded-t-[25px] ${notificationDropdownOpen ? '' : 'rounded-b-[25px]'} h-[50px] w-auto flex items-center justify-center shadow-lg pr-4 px-3 cursor-pointer`}
                                            onClick={
                                                () => {
                                                    const dropdownMenu = document.getElementById('dropdown-menu');
                                                    if (dropdownMenu) {
                                                        // dropdownMenu.classList.toggle('hidden');
                                                        setNotificationDropdownOpen(!notificationDropdownOpen);
                                                        if (!notificationDropdownOpen) {
                                                            console.log("Notification dropdown clicked for open");
                                                            dropdownMenu.classList.remove('hidden');
                                                        } else {
                                                            console.log("Notification dropdown clicked for close");
                                                            dropdownMenu.classList.add('hidden');
                                                        }
                                                    }
                                                }
                                            }
                                        >
                                            <button
                                                id="dropdown-scrollable"
                                                type="button"
                                                className="flex items-center justify-center w-10 h-10 rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors"
                                                aria-haspopup="menu"
                                                aria-expanded="false"
                                                aria-label="Notifications"
                                            >
                                                <div className="relative">
                                                    {unreadCount > 0 && (
                                                        <span className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-red-500 rounded-full">
                                                            {unreadCount}
                                                        </span>
                                                    )}
                                                    <img
                                                        src={`${process.env.PUBLIC_URL}/images/desktop/notification.svg`}
                                                        alt="notification"
                                                        className="w-8 h-8 object-contain"
                                                    />
                                                </div>
                                            </button>
                                        </div>

                                        {/* Dropdown Menu */}
                                        <div className={`absolute right-0 top-full w-[600px] bg-white rounded-lg shadow-lg border border-gray-200 z-[999] hidden`} id="dropdown-menu" role="menu" aria-orientation="vertical" aria-labelledby="dropdown-scrollable">
                                            {/* Header */}
                                            <div className="px-4 py-3 border-b border-gray-200">
                                                <h6 className="text-base font-semibold text-gray-900 text-center">
                                                    Notifications
                                                    {/* {unreadCount > 0 && <span className="text-red-600">({unreadCount})</span>} */}
                                                </h6>
                                            </div>

                            {/* Scrollable Content */}
                            <div className="max-h-[700px] overflow-auto" ref={notificationScrollRef}>
                                {notifications.length === 0 ? (
                                    <div className="flex items-center justify-center py-12 text-gray-500">
                                        <div className="text-center">
                                            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                            </svg>
                                            <p className="text-sm">No notifications</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {notifications.map((notification) => (
                                            <div
                                                key={notification.id}
                                                className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                                                    !notification.isIntracted ? 'bg-blue-50' : ''
                                                }`}
                                            >
                                                <div className="flex-1 min-w-0" onClick={() => handelNotificationClick(notification)}>
                                                    <h6 
                                                        className={`text-base truncate ${
                                                            !notification.isIntracted ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'
                                                        }`}
                                                        style={{ color: notification.color }}
                                                    >
                                                        {notification.title}
                                                    </h6>
                                                    <p className="text-sm text-gray-500 truncate">
                                                        {notification.body}
                                                    </p>
                                                    {!notification.isIntracted && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                            <span className="text-xs text-blue-600">Unread</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-shrink-0 text-xs text-gray-400">
                                                    {notification.createdAt.toDate().toLocaleString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true,
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {/* Loading indicator */}
                                        {isLoadingMore && (
                                            <div className="flex items-center justify-center py-4">
                                                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                                                <span className="ml-2 text-sm text-gray-500">Loading more...</span>
                                            </div>
                                        )}
                                        
                                        {/* End of list indicator */}
                                        {!hasMoreNotifications && notifications.length > 0 && (
                                            <div className="text-center py-4 text-sm text-gray-400 border-t border-gray-100">
                                                No more notifications
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>                                            {/* Footer */}
                                            <div className="border-t border-gray-200">
                                                <button
                                                    onClick={clearAllNotifications}
                                                    className="flex items-center justify-center gap-2 px-4 py-3 w-full text-sm font-medium text-blue-600 hover:bg-gray-50 transition-colors"
                                                >
                                                    {/* <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg> */}
                                                    Clear All
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {/* Notification end */}
                        {/* Screens */}
                        {isAdmin || isManager ? (
                            <div className={`relative group bg-white rounded-t-[25px] ${screensDropdownOpen ? '' : 'rounded-b-[25px]'} h-[50px] w-auto flex items-center shadow-lg pr-4 px-3 cursor-pointer`} onClick={toogleScreensDropdown} >
                                <img src={`${process.env.PUBLIC_URL}/images/desktop/navdownarrow.svg`} alt="dropdown icon" className={`h-8 w-8 mr-2 object-contain transform transition-transform duration-300 `} />
                                <div className="flex flex-col justify-center flex-1">
                                    <div className="flex items-center space-x-1">
                                        <span className="text-sm font-semibold">{`View : ${currentScreen}`}</span>
                                        <img src={`${process.env.PUBLIC_URL}/images/desktop/Naveyeicon.svg`} alt="eye icon" className="w-4 h-4 object-contain" />
                                    </div>
                                    <div className="text-[#02385E] font-montserrat text-[16px] font-normal leading-[19.5px] flex items-center space-x-1">
                                        <span className="text-xs text-gray-500">
                                            {getPageName(location.pathname, currentScreen)}
                                        </span>
                                    </div>

                                </div>
                                {screensDropdownOpen && (
                                    <div
                                        ref={screensdropdownRef}
                                        className="absolute right-0 top-[52px] bg-white shadow-lg rounded-b-[25px] w-full py-2 border-t border-gray-100"
                                    >
                                        {isAdmin && (
                                            <div
                                                className="px-4 py-2 text-sm text-red-500 text-center cursor-pointer rounded-b-[25px] hover:bg-gray-50 transition-colors duration-150"
                                                onClick={() => screenStateHandler('Admin')}
                                            >
                                                Admin
                                            </div>
                                        )}
                                        {(isManager) && (
                                            <div
                                                className="px-4 py-2 text-sm text-red-500 text-center cursor-pointer rounded-b-[25px] hover:bg-gray-50 transition-colors duration-150"
                                                onClick={() => screenStateHandler('Manager')}
                                            >
                                                Manager
                                            </div>
                                        )}
                                        {(isStaff) && (
                                            <div
                                                className="px-4 py-2 text-sm text-red-500 text-center cursor-pointer rounded-b-[25px] hover:bg-gray-50 transition-colors duration-150"
                                                onClick={() => screenStateHandler('Staff')}
                                            >
                                                Staff
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : null}
                        {/* SCreens end */}
                        {/* Profile */}
                        <div className={`relative group bg-white rounded-t-[25px] ${studprofdropdownOpen ? '' : 'rounded-b-[25px]'} h-[50px] w-auto flex items-center shadow-lg pr-4 px-3 cursor-pointer`} onClick={toggleStudprofdropdown} >
                            <img src={profilePicture || profileImage} alt="Profile" className="w-8 h-8 rounded-full mr-2 object-contain" />
                            <div className="flex flex-col text-gray-700 flex-1">
                                <span
                                    className="text-sm font-semibold truncate"
                                    style={{ maxWidth: '115px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    {userName}
                                </span>
                                <span className="text-xs text-gray-500 truncate">{userPosition}</span>
                            </div>
                            {studprofdropdownOpen && (
                                <div
                                    ref={studprofdropdownRef}
                                    className="absolute right-0 top-[52px] bg-white shadow-lg rounded-b-[25px] w-full py-2 border-t border-gray-100"
                                >
                                    <div className="px-4 py-1 text-xs text-gray-400 text-center border-b border-gray-100">
                                        Version Date: {APP_VERSION}
                                    </div>
                                    <div
                                        className="px-4 py-2 text-sm text-red-500 text-center cursor-pointer rounded-b-[25px] hover:bg-gray-50 transition-colors duration-150"
                                        onClick={handleLogout}
                                    >
                                        Logout
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Profile end */}
                    </div>

                </div>
            </nav>
            <div className="w-[100%] mx-auto">
                <div className=" w-full"
                    style={{
                        border: "none",
                        height: "2px",
                        background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 10%, #5E9DCB 85%, #FFFFFF 100%)",
                    }} />
            </div>

            {/* Validity Modal */}
            {isValidityModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-xl">
                        <div className="text-center">
                            <div className="mb-6">
                                <svg className="w-16 h-16 text-orange-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                    {validityStatus?.isExpired ? 'Your license has expired' : 'Your license is about to expire'}
                                </h2>
                            </div>

                            <div className="mb-6 text-gray-600 space-y-3">
                                <p>We hope you are enjoying the benefits of Scholar CRM Professional and we're happy to have you on board.</p>
                                <p>
                                    {validityStatus?.isExpired
                                        ? `Your license expired on ${validityStatus.formattedDate}. You'll need to purchase a license to continue using the service.`
                                        : `We just want to remind you that after ${validityStatus?.formattedDate}, you'll need to purchase a license.`
                                    }
                                </p>
                                <p>Once you have completed your purchase, simply click refresh to validate your license.</p>
                            </div>

                            {/* Countdown Timer */}
                            {timeLeft && !validityStatus?.isExpired && (
                                <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                                    <h3 className="text-lg font-semibold text-orange-800 mb-3">Time Remaining</h3>
                                    <div className="grid grid-cols-4 gap-4 text-center">
                                        <div className="bg-white p-3 rounded-lg shadow-sm">
                                            <div className="text-2xl font-bold text-orange-600">{timeLeft.days}</div>
                                            <div className="text-xs text-gray-500 uppercase">Days</div>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg shadow-sm">
                                            <div className="text-2xl font-bold text-orange-600">{timeLeft.hours}</div>
                                            <div className="text-xs text-gray-500 uppercase">Hours</div>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg shadow-sm">
                                            <div className="text-2xl font-bold text-orange-600">{timeLeft.minutes}</div>
                                            <div className="text-xs text-gray-500 uppercase">Minutes</div>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg shadow-sm">
                                            <div className="text-2xl font-bold text-orange-600">{timeLeft.seconds}</div>
                                            <div className="text-xs text-gray-500 uppercase">Seconds</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setIsValidityModalOpen(false)}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    Close
                                </button>
                                {/* <button
                                    onClick={() => window.location.reload()}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Refresh License
                                </button> */}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Calendar Component */}
            {userRole !== 'subAgent' && (
                <Calendar
                    isOpen={isCalendarOpen}
                    onClose={() => setIsCalendarOpen(false)}
                />
            )}
        </>
    );
};

export default TopNavbar;